import * as React from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { Breadcrumb, Breadcrumbs, FAQItem } from '@type/component-data-types'

type Props = {
  companyName?: string
  siteDescription?: string
  siteTitle?: string
  siteUrl?: string
  description?: string
  breadcrumb?: Breadcrumbs
  publishDate?: string
  modifiedDate?: string
  pageTitle?: string
  faq?: FAQItem[]
  mainEntityOfPage?: string // 記事の正規URL
  about?: string // 記事の主要トピック
  keywords?: string[] // 記事に関連するキーワード配列
  articleImagePath?: string // 記事のOGP画像パス
  children?: React.ReactNode
}

const StructureData = (props: Props): React.JSX.Element => {
  const data = useStaticQuery(graphql`
    query getStructureMetaData {
      site {
        siteMetadata {
          description
          faviconPath
          siteUrl
          companyName
          name
          alternateName
          afbLogoImagePath
          corporateAddressLocality
          corporateAddressRegion
          corporateAddressStreet
          contactPageUrl
          contactPageDescription
          sameAs {
            key
            url
          }
          foundingDate
          author {
            name
            description
          }
        }
      }
    }
  `)

  const shouldRenderStructureDataArticle =
    props.pageTitle && (props.publishDate || props.modifiedDate)

  // 構造化データに設置するコンテンツの初期値処理
  const profilePageUrl = 'https://www.afi-b.com/profile/about-author/',
    companyName = props.companyName || data.site.siteMetadata.companyName,
    siteDescription =
      props.siteDescription || data.site.siteMetadata.description,
    siteUrl = props.siteUrl || data.site.siteMetadata.siteUrl,
    siteName = data.site.siteMetadata.name,
    alternateName = data.site.siteMetadata.alternateName,
    afbLogoImagePath = data.site.siteMetadata.afbLogoImagePath,
    description = props.description || data.site.siteMetadata.description,
    afbAuthor = data.site.siteMetadata.author,
    corporateAddressLocality = data.site.siteMetadata.corporateAddressLocality,
    corporateAddressRegion = data.site.siteMetadata.corporateAddressRegion,
    corporateAddressStreet = data.site.siteMetadata.corporateAddressStreet,
    contactPageUrl = data.site.siteMetadata.contactPageUrl,
    contactPageDescription = data.site.siteMetadata.contactPageDescription,
    sameAsData = data.site.siteMetadata.sameAs,
    foundingDate = data.site.siteMetadata.foundingDate,
    articlePageTitle = props.pageTitle,
    articlePublishDate = props.publishDate,
    articleModifiedDate = props.modifiedDate,
    articleMainEntityOfPage = props.mainEntityOfPage,
    articleAbout = props.about,
    articleKeywords = props.keywords,
    articleImagePath = props.articleImagePath || '', // 記事のOGP画像パス
    faq = props.faq,
    breadcrumbData = props.breadcrumb,
    propsChildren = props.children,
    sameAsUrls = sameAsData.map(
      (item: { key: string; url: string }) => item.url,
    )

  // props.childrenの存在チェック
  const isExistPropsChildren: boolean = propsChildren !== undefined

  // パンくずの構造化データを設置
  let breadcrumbs: Array<string> = []

  // memo: isExistBreadcrumbで判定するとundefinedの可能性が指摘される為、propsからチェックしている
  if (breadcrumbData !== undefined) {
    breadcrumbs = breadcrumbData.map((item: Breadcrumb) => {
      return `{
          "@type": "ListItem",
          "position": "${item.position}",
          "name": "${item.name}",
          "item": "${siteUrl}${item.item}"
        }`
    })
  }

  // memo: 修正する場合はbuild後に構造化データのvalidationでチェックしてください。
  const breadcrumb =
    breadcrumbs.length > 0
      ? `,{"@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement":[
    ${breadcrumbs}
  ]
}`
      : ''

  // Article構造化データの生成
  const articleStructureData = shouldRenderStructureDataArticle
    ? (() => {
        const modifiedDateField = articleModifiedDate
          ? `,"dateModified": "${articleModifiedDate}"`
          : ''
        const datePublishedField = articlePublishDate
          ? `,"datePublished": "${articlePublishDate}"`
          : ''

        // @id: mainEntityOfPageがある場合のみ設定
        const articleId = articleMainEntityOfPage
          ? `,"@id": "${articleMainEntityOfPage}#article"`
          : ''

        // mainEntityOfPage: propsから受け取った正規URL
        const mainEntityOfPageField = articleMainEntityOfPage
          ? `,"mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "${articleMainEntityOfPage}"
          }`
          : ''

        // about: 記事の主要トピック
        const aboutField = articleAbout ? `,"about": "${articleAbout}"` : ''

        // keywords: カンマ区切りのキーワード文字列
        const keywordsField =
          articleKeywords && articleKeywords.length > 0
            ? `,"keywords": "${articleKeywords.join(', ')}"`
            : ''

        // image: 記事の画像（articleImagePathがある場合のみ）
        const imageField = articleImagePath
          ? `,"image": {
            "@type": "ImageObject",
            "url": "${articleImagePath}"
          }`
          : ''

        // hasPart: FAQデータが存在する場合のみFAQPageへの参照
        const hasPart =
          faq && faq.length > 0
            ? `,"hasPart": {
            "@type": "FAQPage",
            "mainEntity": [${faq
              .map(
                item => `{
              "@type": "Question",
              "name": "${item.ask.replace(/"/g, '\\"')}",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "${item.answer.replace(/"/g, '\\"')}"
              }
            }`,
              )
              .join(',')}]
          }`
            : ''

        return `,{
        "@context": "https://schema.org",
        "@type": "Article"${articleId},
        "headline": "${articlePageTitle}",
        "author": {
          "@type": "Organization",
          "name": "afb運用チーム"
        },
        "publisher": {
          "@id": "${siteUrl}#organization"
        },
        "description": "${description}"
        ${imageField}
        ${modifiedDateField}
        ${datePublishedField}
        ${mainEntityOfPageField}
        ${aboutField}
        ${keywordsField}
        ${hasPart}
      }`
      })()
    : ''

  const children = isExistPropsChildren ? `,${propsChildren}` : ''

  return (
    <script type="application/ld+json">
      {`
        [
          {
            "@context": "https://schema.org",
            "@type": "Corporation",
            "@id": "${siteUrl}#organization",
            "name": "${companyName}",
            "alternateName": "For it Inc.",
            "legalName": "${companyName}",
            "logo": "${afbLogoImagePath}",
            "brand": {
              "@type": "Brand",
              "name": "afb"
            },
            "url": "${siteUrl}",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "${corporateAddressLocality}",
              "addressRegion": "${corporateAddressRegion}",
              "streetAddress": "${corporateAddressStreet}"
            },
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "url": "${contactPageUrl}",
                "contactType": "customer service",
                "description": "${contactPageDescription}",
                "areaServed": "JP",
                "availableLanguage": ["Japanese"]
              }
            ],
            "sameAs": [${sameAsUrls.map((url: string) => `"${url}"`).join(',')}],
            "foundingDate": "${foundingDate}"
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "${siteUrl}#website",
            "name": "${siteName}",
            "alternateName": "${alternateName}",
            "url": "${siteUrl}",
            "about": "${siteDescription}",
            "publisher": {
              "@id": "${siteUrl}#organization"
            },
            "author": {
              "@type": "Organization",
              "name": "${afbAuthor.name}",
              "description": "${afbAuthor.description}",
              "url": "${siteUrl}",
              "sameAs": "${siteUrl}"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "url": "${profilePageUrl}",
            "mainEntity": {
              "@type": "Organization",
              "name": "${afbAuthor.name}",
              "alternateName": "${afbAuthor.name}",
              "description": "${afbAuthor.description}"
            }
          }
          ${breadcrumb}
          ${articleStructureData}
          ${children}
        ]
      `}
    </script>
  )
}

export default StructureData
